import { AlertTriangle, RefreshCcw, RotateCcw } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  error: Error | null;
}

/**
 * Last-resort recovery UI. Expected API and worker failures have local error
 * states; this boundary prevents an unexpected render failure from turning
 * the entire application into a blank page.
 */
export class AppErrorBoundary extends Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  override state: AppErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): AppErrorBoundaryState {
    return {
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Trajectory GUI render failure', error, info.componentStack);
  }

  override render() {
    if (!this.state.error) return this.props.children;

    return (
      <main className="flex h-full items-center justify-center bg-canvas p-6 text-text">
        <section
          role="alert"
          className="w-full max-w-xl rounded-panel border border-danger/40 bg-surface-raised p-6 shadow-2xl"
        >
          <AlertTriangle className="h-8 w-8 text-danger" aria-hidden />
          <h1 className="mt-4 text-lg font-semibold">The interface hit an unexpected error</h1>
          <p className="mt-2 text-sm leading-relaxed text-muted">
            Your saved missions remain in the API. Try rebuilding the interface first; reload the
            application if the problem continues.
          </p>
          <pre className="mt-4 max-h-36 overflow-auto rounded-control bg-surface-sunken p-3 text-xs text-danger">
            {this.state.error.message}
          </pre>
          <div className="mt-5 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => this.setState({ error: null })}
              className="flex items-center gap-2 rounded-control bg-accent px-3 py-2 text-xs font-medium text-accent-contrast"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Rebuild interface
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="flex items-center gap-2 rounded-control border border-border px-3 py-2 text-xs text-muted hover:text-text"
            >
              <RefreshCcw className="h-3.5 w-3.5" /> Reload application
            </button>
          </div>
        </section>
      </main>
    );
  }
}
